import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AnimationPage from "../../shared/animation-page/AnimationPage";
import Button from "../../design/Button";
import GModal from "../../design/components/GModal";
import { CustomSwitch } from "../../design/components/customComponents";
import { JService } from "../../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import classes from "./IntegrationSettingsModal.module.css";
import {
  FormControlLabel,
  FormControl,
  Radio,
  RadioGroup,
  Checkbox,
} from "@mui/material";
import { checkboxCheck, checkboxUncheck } from "../../../utility-functions/assetLoader";
import colors from "../../design/colors";
import styles from "../../design/styles";

const IntegrationSettingsModal = ({ visible, closeModal, integrationName }) => {

  const {canManageOrgLevelIntegrations} = useSelector(state => state?.auth?.permissions);

  const [syncActions, setSyncActions] = useState({});
  const [syncFields, setSyncFields] = useState({});
  const [primaryAssociation, setPrimaryAssociation] = useState("Contact");
  const [updateForAllUsers, setUpdateForAllUsers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const retrieveIntegrationSettings = async () => {
    setIsLoading(true);
    try {
      const tempUrl = integrationName?.toLowerCase() === 'deskera' ? 'api/v1/integrations/':'api/v2/integrations/' 
      const res = await JService.get(
        `${tempUrl}${integrationName.toLowerCase()}-sync/`
      );
      if (!res?.success) {
        throw new Error(
          res?.message ||
            `Failed to retrieve ${integrationName.toLowerCase()} integration settings`
        );
      }
      updateDetails(res);
    } catch (err) {
      console.error(
        `Error while retrieving ${integrationName} integration settings: `,
        err
      );
      enqueueSnackbar(
        err.message ||
          `Failed to retrieve ${integrationName} integration settings`,
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const payload = {
        ...(Object.keys(syncActions ?? {})?.length > 0
          ? { sync_action: { ...syncActions } }
          : {}),
        ...(Object.keys(syncFields ?? {})?.length > 0
          ? { sync_fields: { ...syncFields } }
          : {}),
      };
      if (integrationName === "Salesforce") {
        payload["sync_action"].whoid_preference = primaryAssociation;
        if (syncActions["create_contact"]) {
          if (primaryAssociation === "Contact") {
            payload["sync_action"].create_lead = false;
          } else {
            payload["sync_action"].create_contact = false;
            payload["sync_action"].create_lead = true;
          }
        }
      } else if (integrationName === "HubSpot") {
        payload["sync_action"].create_contact_if_missing =
          syncActions["create_contact"];
      } else if (integrationName === "Zoho") {
        payload["update_for_all_users"] = updateForAllUsers;
      }
      const tempUrl = integrationName?.toLowerCase() === 'deskera' ? 'api/v1/integrations/' : 'api/v2/integrations/'
      const res = await JService.post(
        `${tempUrl}${integrationName.toLowerCase()}-sync/`,
        payload
      );
      if (!res?.success) {
        throw new Error(
          res?.message ||
            `Failed to update ${integrationName} integration settings`
        );
      }
      updateDetails(res);
      enqueueSnackbar("Changes updated successfully", {
        variant: "success",
      });
      closeModal();
    } catch (err) {
      console.error(
        `Error while updating ${integrationName} integration settings: `,
        err
      );
      enqueueSnackbar(
        err.message ||
          `Failed to update ${integrationName} integration settings`,
        { variant: "error" }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDetails = (data) => {
    const syncActionsData = data?.sync_action || {};
    if (integrationName === "Salesforce") {
      setPrimaryAssociation(data?.sync_action?.whoid_preference || "Contact");
      if (syncActionsData.create_lead) {
        syncActionsData.create_contact = true;
      }
    } else if (integrationName === "Zoho") {
      setUpdateForAllUsers(data?.update_for_all_users);
    }
    setSyncActions(syncActionsData);
    setSyncFields(data?.sync_fields || {});
  };

  const handleCancel = () => {
    setSyncActions({});
    setSyncFields({});
    setPrimaryAssociation("Contact");
    setUpdateForAllUsers(false);
    closeModal();
  };

  const handleSyncActionsChange = (e) => {
    const value = e.target.checked;
    const field = e.target.name;
    setSyncActions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSyncFieldsChange = (e) => {
    const value = e.target.checked;
    const field = e.target.name;
    setSyncFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (!visible) return;
    retrieveIntegrationSettings();
  }, [visible]);

  return (
    <GModal
      visible={visible}
      heading={`${integrationName} integration settings`}
      closeModal={handleCancel}
      bottomButtons={[
        <Button
          key="cancel"
          buttonText="Cancel"
          variant="outlined"
          hierarchy="white"
          onClick={handleCancel}
          styleOverride={{
            fontWeight: 500,
            height: "36px",
            width: "75px",
          }}
        />,
        <Button
          key="save"
          buttonText="Save"
          variant="contained"
          hierarchy="green"
          isLoading={isUpdating}
          onClick={handleSave}
          styleOverride={{
            fontWeight: 500,
            height: "36px",
            width: "61px",
          }}
        />,
      ]}
      body={
        isLoading ? (
          <AnimationPage />
        ) : (
          <div className={classes.container}>
            <div className={classes.section}>
              <h4 className="t7 nc-gray-900 medium-font">Sync actions</h4>
              <ul className={classes.list}>
                {Object.entries(
                  integrationSyncActions[integrationName] ?? {}
                )?.map(([action, details]) => {
                  return (
                    <CustomSwitch
                      label={details.label}
                      description={details.description}
                      checked={syncActions[action]}
                      onChange={handleSyncActionsChange}
                      name={action}
                      key={action}
                    />
                  );
                })}
                {integrationName === "Salesforce" && (
                  <FormControl>
                    <p className="t7 nc-gray-900 regular-font">
                      Primary call association
                    </p>
                    <p
                      className="t7 nc-gray-600 regular-font"
                      style={{ marginTop: "2px" }}
                    >
                      Object to associate with the call in salesforce
                    </p>
                    <RadioGroup
                      aria-labelledby="radio-buttons-group-label"
                      name="radio-buttons-group"
                      value={primaryAssociation}
                      onChange={(e) => setPrimaryAssociation(e.target.value)}
                    >
                      <div style={{ display: "flex" }}>
                        <FormControlLabel
                          value="Contact"
                          control={<Radio sx={localStyles.radioStyle} />}
                          sx={{
                            "& .MuiTypography-root": {
                              fontSize: "14px",
                            },
                          }}
                          label="Contact"
                        />
                        <FormControlLabel
                          value="Lead"
                          control={<Radio sx={localStyles.radioStyle} />}
                          sx={{
                            "& .MuiTypography-root": {
                              fontSize: "14px",
                            },
                          }}
                          label="Lead"
                        />
                      </div>
                    </RadioGroup>
                  </FormControl>
                )}
              </ul>
            </div>
            {integrationSyncFields[integrationName] &&
              Object.keys(integrationSyncFields[integrationName])?.length >
                0 && (
                <div className={classes.section}>
                  <h4 className="t7 nc-gray-900 medium-font">Sync fields</h4>
                  <ul className={classes.list}>
                    {Object.entries(
                      integrationSyncFields[integrationName] ?? {}
                    )?.map(([field, value]) => {
                      const isObj = typeof value === "object";
                      return (
                        <CustomSwitch
                          label={isObj ? value.label: value}
                          description={isObj ? value.description : undefined}
                          checked={syncFields[field]}
                          onChange={handleSyncFieldsChange}
                          name={field}
                          key={field}
                        />
                      );
                    })}
                  </ul>
                </div>
              )}
            {integrationCustomFields[integrationName] &&
              Object.keys(integrationCustomFields[integrationName])?.length >
                0 && (
                <div className={classes.section}>
                  <h4 className="t7 nc-gray-900 medium-font">
                    Sync custom fields
                  </h4>
                  <ul className={classes.list}>
                    {integrationCustomFields[integrationName]?.map((field) => {
                      return (
                        <CustomSwitch
                          label={field}
                          checked={syncFields[field]}
                          onChange={handleSyncFieldsChange}
                          name={field}
                          key={field}
                        />
                      );
                    })}
                  </ul>
                </div>
              )}
            {integrationName === "Zoho" && (
              <FormControlLabel control={<Checkbox
                disableRipple={true}
                disabled={!canManageOrgLevelIntegrations}
                checkedIcon={checkboxCheck()}
                icon={checkboxUncheck()}
                sx={{
                  color: colors.nc_gray_400,
                  marginRight: "4px",
                  "&.Mui-checked": {
                    color: colors.ic_green_300,
                  },
                }}
                checked={updateForAllUsers}
                onChange={(e) => setUpdateForAllUsers(e.target.checked)}
                
              />} 
              label="Apply changes to all the users connected to Zoho"
              sx={{".MuiFormControlLabel-label": {...styles.t7, ...styles.regular_font}}}
              />
            )

            }
            {integrationName === "Salesforce" && (
              <p className="t7 nc-gray-900 regular-font">
                Make sure to follow these steps before adding custom fields:{" "}
                <a
                  href="https://knowledge.frejun.com/what-data-is-synced-between-frejun-and-salesforce"
                  target="_blank"
                  className={classes.link}
                >
                  View docs
                </a>
              </p>
            )}
          </div>
        )
      }
      headingStyle={{
        fontWeight: "600",
        fontSize: "16px",
      }}
      modalStyle={{
        width: "478px",
        minWidth: "478px",
        maxHeight: "70vh",
      }}
    />
  );
};

export default IntegrationSettingsModal;

const integrationSyncActions = {
  HubSpot: {
    sync_contacts: {
      label: "Sync contacts",
      description: "Sync contacts created in HubSpot back to FreJun",
    },
    create_contact: {
      label: "Create missing contacts",
      description: "Create contact in HubSpot for unknown numbers",
    },
    associate_all_contacts: {
      label: "Associate call with all contacts",
      description:
        "Associate the call with all HubSpot contacts matching the number, along with their companies and deals",
    },
  },
  Deskera: {
    sync_contacts: {
      label: "Sync contacts",
      description: "Sync contacts created in Deskera back to FreJun",
    },
  },
  Freshworks: {
    create_contact: {
      label: "Create missing contacts",
      description: "Create contact in Freshworks for unknown numbers",
    },
  },
  Salesforce: {
    create_contact: {
      label: "Create missing contact or lead",
      description:
        "Create contact or lead in Salesforce for unknown numbers based on primary call association",
    },
  },
  Zoho: {
    "create_ticket_on_incoming_call": {
      label: "Create ticket on incoming call",
      description: "Create a ticket in Zoho Desk on incoming call"
    },
    "create_ticket_on_outgoing_call": {
      label: "Create ticket on outgoing call",
      description: "Create a ticket in Zoho Desk on outgoing call"
    }
  }
};

const integrationSyncFields = {
  HubSpot: {
    creator_number: "User virtual number",
    candidate_number: "Contact phone number",
    call_status: "Call status",
    sharable_recording_url: "Call recording URL",
    notes: "Notes",
    call_reason: "Call stage",
    call_outcome: "Call outcome",
    call_insights: "Transcript and AI insights"
  },
  Deskera: {},
  Freshworks: {},
  Salesforce: {
    CallType: "Call type",
    ActivityDate: "Activity date",
    CallDurationInSeconds: "Call duration",
    CallDisposition: "Call disposition",
    TaskSubtype: "Task subtype",
    WhoId: "Who Id",
    Description: "Description",
    OwnerId: "Owner Id",
  },
  Zoho: {
    call_insights_and_transcript:{
      label: "Call Insights and transcript",
      description: "Sync call insights and transcript to Zoho Desk and Zoho CRM"
    }
  }
};

const integrationCustomFields = {
  Salesforce: [
    "call_recording__c",
    "Call_Status__c",
    "Call_Transcript__c",
    "Client_Full_Name__c",
    "Client_Phone_Number__c",
  ],
};

const localStyles = {
  radioStyle: {
    fontSize: "14px",
    "&, &.Mui-checked": {
      color: "#2EB358",
    },
  },
};
